import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Mail, MapPin, ChevronDown, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [gitesOpen, setGitesOpen] = useState(false);
  const [gites, setGites] = useState([]);
  const { pathname } = useLocation();
  const { t, tg } = useI18n();

  const LINKS = [
    { to: "/decouvrir-haut-doubs", label: "Découvrir le Haut-Doubs" },
    { to: "/avis", label: t("nav.reviews") },
    { to: "/contact", label: t("nav.contact") },
  ];

  useEffect(() => {
    api.get("/gites").then((r) => setGites(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    setOpen(false);
    setGitesOpen(false);
  }, [pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-ink/85 backdrop-blur-xl">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <div className="flex h-24 items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="group">
            <span className="block font-display text-xl tracking-[0.18em] text-bone">LA FERME COMTOISE</span>
            <span className="mt-1 block text-[10px] uppercase tracking-[0.4em] text-brass">Les Fins · Haut-Doubs</span>
          </Link>

          <nav className="hidden items-center gap-10 lg:flex">
            <div
              className="relative"
              onMouseEnter={() => setGitesOpen(true)}
              onMouseLeave={() => setGitesOpen(false)}
            >
              <button
                className="flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-bone/80 hover:text-brass"
                data-testid="nav-gites"
                onClick={() => setGitesOpen(true)}
                style={{ transition: "color .35s ease" }}
              >
                {t("nav.gites")} <ChevronDown size={13} className={gitesOpen ? "rotate-180" : ""} style={{ transition: "transform .3s ease" }} />
              </button>
              {gitesOpen && (
                <div
                  className="absolute left-0 top-full w-[330px] border border-white/10 bg-panel/95 backdrop-blur-xl"
                  data-testid="gites-dropdown"
                >
                  {gites.map(tg).map((g) => (
                    <Link
                      key={g.slug}
                      to={`/gite/${g.slug}`}
                      data-testid={`nav-gite-${g.slug}`}
                      className="block border-b border-white/[0.07] px-6 py-5 last:border-0 hover:bg-white/[0.04]"
                      style={{ transition: "background-color .3s ease" }}
                    >
                      <span className="block font-display text-lg text-bone">{g.name}</span>
                      <span className="mt-1 block text-[10px] uppercase tracking-[0.22em] text-brass">{g.nav_note}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {LINKS.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.slice(1)}`}
                className={({ isActive }) =>
                  `link-underline text-[12px] uppercase tracking-[0.22em] ${isActive ? "text-brass" : "text-bone/80 hover:text-brass"}`
                }
                style={{ transition: "color .35s ease" }}
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/reservation" className="btn-primary !px-7 !py-3" data-testid="header-book-btn">
              {t("nav.book")}
            </Link>
            <LanguageSwitcher />
            <Link to="/admin" data-testid="nav-admin"
              className="flex items-center gap-1.5 border border-brass/40 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-brass hover:bg-brass hover:text-ink"
              style={{ transition: "background-color .3s ease, color .3s ease" }}>
              <KeyRound size={13} /> {t("footer.owner")}
            </Link>
          </nav>

          <div className="flex items-center gap-2 lg:hidden">
            <LanguageSwitcher />
            <button className="text-bone" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/10 bg-ink px-6 py-6 lg:hidden" data-testid="mobile-menu">
          <p className="eyebrow">{t("nav.gites")}</p>
          {gites.map(tg).map((g) => (
            <Link key={g.slug} to={`/gite/${g.slug}`} className="mt-4 block" data-testid={`mnav-gite-${g.slug}`}>
              <span className="block font-display text-lg text-bone">{g.name}</span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-brass">{g.nav_note}</span>
            </Link>
          ))}
          <div className="rule my-6" />
          {LINKS.map((n) => (
            <Link key={n.to} to={n.to} className="block py-3 text-[12px] uppercase tracking-[0.22em] text-bone/80">
              {n.label}
            </Link>
          ))}
          <Link to="/admin" onClick={() => setOpen(false)} data-testid="mnav-admin"
            className="mt-3 flex items-center justify-center gap-2 border border-brass/50 py-3 text-[11px] uppercase tracking-[0.2em] text-brass">
            <KeyRound size={14} /> {t("footer.owner")}
          </Link>
          <Link to="/reservation" className="btn-primary mt-3 w-full" data-testid="mnav-book">{t("nav.book")}</Link>
        </div>
      )}
    </header>
  );
};

export const Footer = () => {
  const { t } = useI18n();
  return (
    <footer className="mt-32 border-t border-white/10 bg-ink">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-6 py-20 sm:px-10 md:grid-cols-3">
        <div>
          <p className="font-display text-2xl tracking-[0.14em] text-bone">LA FERME COMTOISE</p>
          <p className="mt-6 max-w-xs text-sm leading-relaxed text-bone/70">{t("footer.tagline")}</p>
        </div>
        <div className="space-y-4 text-sm text-bone/72">
          <p className="eyebrow">{t("footer.find")}</p>
          <p className="flex items-center gap-3"><MapPin size={14} className="text-brass" /> 2 rue de la Gypserie, 25500 Les Fins</p>
          <a href="mailto:contact@lafermecomtoise.com" className="flex items-center gap-3 hover:text-brass"
            data-testid="footer-email" style={{ transition: "color .3s ease" }}>
            <Mail size={14} className="text-brass" /> contact@lafermecomtoise.com
          </a>
          <a
            href="https://www.instagram.com/lafermecomtoisegites/"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-instagram"
            className="flex items-center gap-3 hover:text-brass"
            style={{ transition: "color .3s ease" }}
          >
            <Instagram size={14} className="text-brass" /> @lafermecomtoisegites
          </a>
        </div>
        <div className="space-y-3 text-sm text-bone/72">
          <p className="eyebrow">{t("footer.domain")}</p>
          <Link to="/gite/chalet-6" className="block hover:text-brass" style={{ transition: "color .3s ease" }}>{t("footer.chalet")}</Link>
          <Link to="/gite/gite-4" className="block hover:text-brass" style={{ transition: "color .3s ease" }}>{t("footer.ferme")}</Link>
          <Link to="/gite/ensemble-10" className="block hover:text-brass" style={{ transition: "color .3s ease" }}>{t("footer.both")}</Link>
          <Link to="/reservation" className="block hover:text-brass" style={{ transition: "color .3s ease" }}>{t("nav.book")}</Link>
          <div className="pt-4 text-[11px] uppercase tracking-[0.16em] text-bone/72">
            <Link to="/cgv" className="block py-1 hover:text-brass" data-testid="footer-cgv">{t("footer.cgv")}</Link>
            <Link to="/annulation" className="block py-1 hover:text-brass" data-testid="footer-annulation">{t("footer.cancel")}</Link>
            <Link to="/mentions-legales" className="block py-1 hover:text-brass" data-testid="footer-mentions">{t("footer.legal")}</Link>
            <Link to="/confidentialite" className="block py-1 hover:text-brass" data-testid="footer-confidentialite">{t("footer.privacy")}</Link>
            <Link to="/admin" className="block py-1 text-bone/62 hover:text-brass" data-testid="footer-admin-link">{t("footer.owner")}</Link>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 py-7 text-center text-[10px] uppercase tracking-[0.28em] text-bone/62">
        © {new Date().getFullYear()} {t("footer.bottom")}
      </div>
    </footer>
  );
};

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-ink">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
